import Close from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import {
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Link,
	Tooltip,
	Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetFileNoHistoryQuery } from '@/api/member-management/memberDuplication';
import { FileNoHistoryDTO } from '@/api/DTO/member-management/memberDuplication';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import MemberStatusChip from '@/common/components/MemberStatusChip';
import PreAdmissionChip from '@/common/components/PreAdmissionChip';

interface Props {
	userId?: string;
	fileNo?: string | null;
	hasFileNoHistory?: boolean;
}

const getRecordLink = (row: FileNoHistoryDTO) => {
	switch (row.recordType) {
		case 'PRE_ADMISSION':
			return `/pre-admission/pre-admission-list/detail/${row.userId}`;
		case 'MEMBER':
			return `/member-management/member-list/detail/${row.userId}`;
		default:
			return `/member-management/admission-request-list/detail/${row.userId}`;
	}
};

const formatDate = (date?: string | null) =>
	date ? dayjs(date).format(DISPLAY_DATE_FORMAT) : '-';

type HistoryRow = FileNoHistoryDTO & { rowId: string };

/** Still a Pre-admission, so it has no member status yet. */
const isPreAdmissionRow = (row: FileNoHistoryDTO) =>
	row.recordType === 'PRE_ADMISSION' || row.status == null;

/**
 * Indicator next to File no. that opens every File no. of the person
 * (DMS-REQ-WEB-CHA-001 §4.3).
 */
export default function FileNoHistoryButton({
	userId,
	fileNo,
	hasFileNoHistory,
}: Props) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);

	const canView = !!userId && !!hasFileNoHistory;

	const { data, isFetching } = useGetFileNoHistoryQuery(userId || '', {
		skip: !open || !canView,
		refetchOnMountOrArgChange: true,
	});

	if (!canView) return null;

	const rows: HistoryRow[] = (data?.data || []).map((row, index) => ({
		...row,
		rowId: `${row.userId ?? row.fileNo}-${index}`,
	}));

	const columns: GridColDef<HistoryRow>[] = [
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 16,
			renderCell: (params) => {
				const { row } = params;
				// No File no. yet: fall back to the Pre-admission no.
				const showsPreAdmissionNo =
					row.source !== 'EXCEL' && !row.fileNo && !!row.preAdmissionNo;
				const label = row.fileNo || row.preAdmissionNo || '-';
				const value =
					row.clickable && row.userId && !row.current ? (
						<Link
							href={getRecordLink(row)}
							target='_blank'
							rel='noopener noreferrer'
							variant='body2'
							color={showsPreAdmissionNo ? 'text.secondary' : 'primary'}
						>
							{label}
						</Link>
					) : (
						<Typography
							variant='body2'
							color={showsPreAdmissionNo ? 'text.secondary' : undefined}
						>
							{label}
						</Typography>
					);
				return (
					<Box
						display='flex'
						alignItems='center'
						flexWrap='wrap'
						gap={1}
						paddingY={1}
					>
						{showsPreAdmissionNo ? (
							<Tooltip title={t('memberDuplication.preAdmissionNoTips')}>
								{value}
							</Tooltip>
						) : (
							value
						)}
						{row.current && (
							<Chip
								label={t('memberDuplication.currentRecord')}
								sx={{
									backgroundColor: 'common.tertiary90',
									color: '#353B02',
								}}
							/>
						)}
						{row.source === 'EXCEL' && (
							<Chip label={t('memberDuplication.excel')} />
						)}
					</Box>
				);
			},
		},
		{
			field: 'statusDesc',
			headerName: t('member.status'),
			flex: 14,
			renderCell: (params) => {
				const { row } = params;
				if (row.source === 'EXCEL') return '-';
				// No member status yet: fall back to the Pre-admission status.
				if (isPreAdmissionRow(row)) {
					const label = row.preAdmissionStatusDesc ?? row.statusDesc;
					if (!label) return '-';
					return (
						<PreAdmissionChip
							status={row.preAdmissionStatus ?? undefined}
							label={label}
						/>
					);
				}
				if (!row.statusDesc) return '-';
				return (
					<MemberStatusChip
						status={row.status ?? undefined}
						label={row.statusDesc}
					/>
				);
			},
		},
		{
			field: 'admissionDate',
			headerName: t('member.admissionDate'),
			flex: 10,
			renderCell: (params) => formatDate(params.row.admissionDate),
		},
		{
			field: 'dateOfExitService',
			headerName: t('member.dateExitService'),
			flex: 10,
			renderCell: (params) => formatDate(params.row.dateOfExitService),
		},
	];

	return (
		<>
			<Tooltip title={t('memberDuplication.fileNoHistoryTitle')}>
				<IconButton
					size='small'
					color='secondary'
					aria-label={t('memberDuplication.fileNoHistoryTitle')}
					// Without a File no. the icon lines up with the label.
					sx={{ marginLeft: fileNo ? 0.5 : '-5px' }}
					onClick={() => setOpen(true)}
				>
					<HistoryIcon fontSize='small' />
				</IconButton>
			</Tooltip>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				maxWidth='lg'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{t('memberDuplication.fileNoHistoryTitle')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => setOpen(false)}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					{isFetching ? (
						<CircularProgress size={24} />
					) : (
						<LocalDataGrid
							rows={rows}
							columns={columns}
							getRowId={(row: HistoryRow) => row.rowId}
							pagination={false}
						/>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
